import { observer } from 'mobx-react';

import {
  DollarOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  PlusOutlined,
  StopOutlined,
} from '@ant-design/icons';
import DealershipChooser from 'components/DealershipChooser';
import PitstopSimpleForm from 'components/PitstopSimpleForm';
import PropTypes from 'prop-types';
import { customFleets, PitstopButton, webServiceProvider } from 'shared';
import { AppStore, ShopStore, WorkOrderStore } from 'stores';
import styled from 'styled-components';
import SubLocationDropdown from './SubLocationDropdown';

import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Select,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Spin,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { measurementUnits } from '../../shared/utils';

const { TextArea } = Input;
const { Text } = Typography;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 25px;
`;

const Wrapper = styled.div`
  position: relative;

  .hide-label {
    label {
      visibility: hidden;
    }
  }
`;

const NoImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
  border-radius: 5px;

  height: 200px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 215px;
  height: 215px;
  overflow: hidden;
  background-color: #f0f0f0;
`;

const UploaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;

  .ant-upload-picture-card-wrapper {
    width: auto;
    margin-bottom: 20px;
  }

  .item {
    margin-right: 20px;
    margin-bottom: 20px;
    position: relative;
    display: flex;
    align-items: center;

    width: 100px;
    height: 100px;
    background-color: #d5d3d3;

    &.has-error {
      border: solid 2px red;
      border-style: dotted;
    }

    .remove-button {
      position: absolute;
      top: -11px;
      right: -10px;
      opacity: 1;
      background-color: #333333;
      color: white;

      &:hover {
        background-color: black;
        color: white;
        border-color: black;
      }
    }
  }

  .upload-list-inline .ant-upload-list-item .ant-upload-list-item-actions {
    top: 0;
    right: 60px;
    opacity: 1;

    a {
      color: red;
    }

    i {
      color: red;
    }
  }
`;

const ImageUploadWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  align-items: center;
  justify-content: center;
`;

// 10000000 = 10MB
// 1000000 = 1MB
const MAX_FILE_SIZE = 10000000;

const AddPartPage = ({ history, match }) => {
  const [form] = Form.useForm();

  const [state, setState] = useState({
    loading: false,
    purchaseHistoryData: [],
    shopParts: [],
    currentPart: {},
    previewVisible: false,
    previewImage: '',
    fileList: [],
    partImage: null,
    assetMakeDataSource: [],
    assetTypeDataSource: ['Vehicle', 'Trailer', 'Misc.'],
    uploadErrorMessage: '',
  });

  useEffect(() => {
    getShopParts();
  }, []);

  const navigateToList = () => {
    history.push('/parts');
  };

  const getShopParts = async () => {
    try {
      const parts = await WorkOrderStore.getShopParts(
        ShopStore.currentShop.id,
        {
          include_deleted: false,
        }
      );
      setState((prev) => ({ ...prev, shopParts: parts || [] }));
    } catch (error) {
      AppStore.addError(error.message);
    }
  };

  const uploadImages = async (partId) => {
    const { fileList } = state;
    if (fileList.length > 0) {
      AppStore.addLoading('Uploading image...');
      const partImages = [];
      await Promise.all(
        fileList.map(async (file) => {
          const fd = new FormData();
          fd.append('singleInputFileName', file.originFileObj);
          try {
            const data = await webServiceProvider.postFormData(
              'dashboard/v1/uploadimage',
              fd
            );
            if (data.fileUrl) {
              partImages.push(data.fileUrl);
            }
          } catch {
            AppStore.addError(
              `Image ${state.fileList[0].name} uploaded failed!`
            );
          }
        })
      );
      AppStore.removeLoading('Uploading images...');
      AppStore.addLoading('Updating part...');
      try {
        await WorkOrderStore.updatePartOnCustomEndpoint({
          id: partId,
          images: partImages.join(','),
        });
      } catch {
        AppStore.addError('Failed to update part with images.');
      } finally {
        AppStore.removeLoading('Updating part...');
      }
    }
    return Promise.resolve();
  };

  const onFinish = async (values) => {
    // Check the images to see if they are too big
    const tooBigImages = state.fileList.filter(
      (file) => file.size && file.size > MAX_FILE_SIZE
    );
    if (tooBigImages.length > 0) {
      AppStore.addError('Image too big. Must be less than 10MB.');
      return;
    }

    try {
      setState((prev) => ({ ...prev, loading: true }));
      const { description, number, subLocation } = values;
      const getName = () =>
        number ? `${number} - ${description}` : description;

      const part = await WorkOrderStore.createPartOnCustomEndpoint({
        id_shop: ShopStore.currentShop.id,
        name: getName(),
        sub_location: subLocation,
      });

      await WorkOrderStore.updatePartOnCustomEndpoint({
        id: part.id,
        ...values,
      });

      AppStore.addSuccess('The part was created!');
      await uploadImages(part.id);
      history.push('/parts');
    } catch (error) {
      AppStore.addError(error.message);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setState((prev) => ({
      ...prev,
      previewImage: file.url || file.preview,
      previewVisible: true,
    }));
  };

  const handleChange = async ({ fileList }) => {
    const newStateToSet = { fileList };
    if (fileList.length > 0) {
      const tooBigImages = fileList.filter((file) => file.size > MAX_FILE_SIZE);
      if (tooBigImages.length) {
        newStateToSet.fileList = fileList.filter(
          (file) => file.size <= MAX_FILE_SIZE
        );
        setState((prev) => ({
          ...prev,
          ...newStateToSet,
          uploadErrorMessage: 'Image too big. Must be less than 10MB.',
        }));
      } else {
        const base64Image = await getBase64(
          fileList[0].originFileObj || fileList[0].url
        );
        setState((prev) => ({
          ...prev,
          ...newStateToSet,
          partImage: base64Image,
          uploadErrorMessage: '',
        }));
      }
    } else {
      setState((prev) => ({
        ...prev,
        ...newStateToSet,
        partImage: null,
      }));
    }
  };

  const handleRemove = (event, fileUid) => {
    event.stopPropagation();
    const newFileList = state.fileList.filter((file) => file.uid !== fileUid);
    setState((prev) => ({ ...prev, fileList: newFileList }));
    handleChange({ fileList: newFileList });
  };

  if (state.loading) {
    return (
      <LoadingContainer>
        <Spin />
      </LoadingContainer>
    );
  }

  return (
    <>
      <Wrapper>
        <div style={{ position: 'absolute', right: '0px' }}>
          <PitstopButton
            type="default"
            icon={<LeftOutlined />}
            onClick={navigateToList}
          >
            Back to Parts
          </PitstopButton>
        </div>
        <h1>Add Part</h1>

        <DealershipChooser />

        <Row gutter={8}>
          <Col offset={3} span={14}>
            <PitstopSimpleForm
              form={form}
              submitLabel="Save Part"
              onFinish={onFinish}
              initialValues={state.currentPart}
              layout="vertical"
            >
              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item
                    label="Part Number"
                    name="number"
                    help={
                      customFleets.ufpFleet.includes(
                        ShopStore.currentShop.parentShopId ||
                          ShopStore.currentShop.id
                      )
                        ? 'Internal part identifier. Must be unique per location.'
                        : 'Internal part identifier. Must be unique per part.'
                    }
                    rules={[
                      {
                        required: true,
                        message: 'Please input the Part Number!',
                      },
                      {
                        validator: async (_, value) => {
                          if (!value) return;

                          const dupPart = state.shopParts.find((part) => {
                            return (
                              String(part.number).trim() === value.trim() &&
                              (!customFleets.ufpFleet.includes(
                                ShopStore.currentShop.parentShopId ||
                                  ShopStore.currentShop.id
                              ) ||
                                (customFleets.ufpFleet.includes(
                                  ShopStore.currentShop.parentShopId ||
                                    ShopStore.currentShop.id
                                ) &&
                                  value.trim() &&
                                  part.sub_location ===
                                    form.getFieldValue('subLocation')))
                            );
                          });

                          if (dupPart) {
                            throw new Error(
                              (
                                <>
                                  <Typography.Text type="danger">
                                    A part with this number already exists in
                                    your inventory. To avoid duplicates,
                                    increase its stock count instead of adding a
                                    new part.{' '}
                                    <Link to={`/parts/${dupPart.id}`}>
                                      Go to Existing Part
                                    </Link>
                                  </Typography.Text>
                                </>
                              )
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label="Part Description"
                    name="description"
                    rules={[
                      {
                        required: true,
                        message: 'Description is required.',
                      },
                      {
                        max: 255,
                        message:
                          'The description needs to be less than 255 characters.',
                      },
                    ]}
                  >
                    <TextArea rows={2} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  {state.partImage ? (
                    <ImageWrapper>
                      <img
                        src={state.partImage}
                        alt="part"
                        style={{
                          width: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </ImageWrapper>
                  ) : (
                    <NoImageWrapper>
                      <InboxOutlined
                        style={{ fontSize: '50px', color: 'lightgrey' }}
                      />
                      <div style={{ color: 'lightgrey' }}>No images added</div>
                    </NoImageWrapper>
                  )}
                </Col>
              </Row>

              {/* Asset type and make */}
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="Asset Type" name="type">
                    <AutoComplete
                      options={state.assetTypeDataSource.map((type) => ({
                        value: type,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Asset Make" name="make">
                    <AutoComplete
                      options={state.assetMakeDataSource.map((make) => ({
                        value: make,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={6}>
                  <Form.Item
                    label="Current Stock"
                    name="currentStock"
                    rules={[
                      {
                        required: true,
                        message: 'Current Stock is required.',
                      },
                      {
                        validator: (_, value) => {
                          if (value < 0) {
                            return Promise.reject('Stock cannot be negative.');
                          }
                          if (value % 1 !== 0) {
                            return Promise.reject('Stock cannot be a decimal.');
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          const stockLimit = form.getFieldValue('stocklimit');
                          if (
                            stockLimit !== undefined &&
                            value !== undefined &&
                            value > stockLimit
                          ) {
                            return Promise.reject(
                              'Current stock cannot be greater than stock limit.'
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      prefix={
                        <StopOutlined style={{ color: 'rgba(0,0,0,.25)' }} />
                      }
                      onChange={() => {
                        form.validateFields(['stocklimit']);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="volumeunit"
                    label="Meas. Unit"
                    placeholder={''}
                    onChange={(val) => {
                      form.setFieldsValue({ volumeunit: val });
                    }}
                  >
                    <Select style={{ width: '100%' }}>
                      <Select.Option value="">-</Select.Option>
                      {measurementUnits.map((unit) => (
                        <Select.Option key={unit} value={unit}>
                          {unit}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Stock Limit"
                    name="stocklimit"
                    rules={[
                      {
                        required: true,
                        message: 'Stock limit is required.',
                      },
                      {
                        validator: (_, value) => {
                          if (value < 0) {
                            return Promise.reject(
                              'Stock limit cannot be negative.'
                            );
                          }
                          if (value % 1 !== 0) {
                            return Promise.reject(
                              'Stock limit cannot be a decimal.'
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          const currentStock =
                            form.getFieldValue('currentStock');
                          if (
                            currentStock !== undefined &&
                            value !== undefined &&
                            currentStock > value
                          ) {
                            return Promise.reject(
                              'Current stock cannot be greater than stock limit.'
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      prefix={
                        <StopOutlined style={{ color: 'rgba(0,0,0,.25)' }} />
                      }
                      onChange={() => {
                        form.validateFields(['currentStock']);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Unit Cost" name="cost">
                    <InputNumber
                      style={{ width: '100%' }}
                      prefix={
                        <DollarOutlined style={{ color: 'rgba(0,0,0,.25)' }} />
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              {ShopStore.currentShop.id === 374 && (
                <Row gutter={8}>
                  <Col span={8}>
                    <Form.Item label="Warranty Start" name="warrantyStart">
                      <DatePicker
                        format="MM/DD/YYYY"
                        style={{ width: '100%' }}
                        showTime
                        placeholder="Select start date"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Warranty End" name="warrantyEnd">
                      <DatePicker
                        format="MM/DD/YYYY"
                        style={{ width: '100%' }}
                        showTime
                        placeholder="Select end date"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Vendor" name="vendor">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item label="Aisle / Shelf / Bin" name="aisle">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label=" " name="shelf" className="hide-label">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label=" " name="bin" className="hide-label">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              {customFleets.ufpFleet.includes(
                ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
              ) && (
                <Row gutter={8}>
                  <Col span={8}>
                    <Form.Item label="Sub-location" name="subLocation">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <SubLocationDropdown
                          shopId={ShopStore.currentShop.id}
                          onChange={(value) =>
                            form.setFieldValue('subLocation', value)
                          }
                        />
                        <Tooltip title="Add sub-locations for your parts based on your Samsara tagging structure">
                          <InfoCircleOutlined
                            style={{ marginLeft: 8, color: '#1890ff' }}
                          />
                        </Tooltip>
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={8}>
                <Col span={24}>
                  <Form.Item label="Additional Notes:" name="additionalNotes">
                    <TextArea rows={4} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={24}>
                  <Text>
                    Add images (the first image uploaded will be used for the
                    preview up above):
                  </Text>
                  <UploaderWrapper>
                    {state.fileList.map((file) => {
                      const base64Image = file.originFileObj
                        ? URL.createObjectURL(file.originFileObj)
                        : file.url;
                      return (
                        <div
                          key={file.uid}
                          className={
                            file.status === 'error' ? 'item has-error' : 'item'
                          }
                        >
                          <img
                            src={base64Image}
                            alt="part"
                            style={{
                              width: '100%',
                              maxHeight: '100%',
                            }}
                          />
                          <Button
                            className="remove-button"
                            size="small"
                            onClick={(event) => handleRemove(event, file.uid)}
                            shape="circle"
                            icon={<StopOutlined />}
                          />
                        </div>
                      );
                    })}
                    <Upload
                      accept="image/*"
                      listType="picture-card"
                      fileList={state.fileList}
                      onPreview={handlePreview}
                      onChange={handleChange}
                      beforeUpload={() => false}
                      className="upload-list-inline"
                      showUploadList={false}
                    >
                      <ImageUploadWrapper>
                        <PlusOutlined style={{ fontSize: 13 }} />
                        Upload
                      </ImageUploadWrapper>
                    </Upload>
                    {state.uploadErrorMessage && (
                      <div style={{ color: 'red' }}>
                        {state.uploadErrorMessage}
                      </div>
                    )}
                  </UploaderWrapper>
                  <Modal
                    visible={state.previewVisible}
                    footer={null}
                    onCancel={() =>
                      setState((prev) => ({ ...prev, previewVisible: false }))
                    }
                  >
                    <img
                      alt="example"
                      style={{ width: '100%' }}
                      src={state.previewImage}
                    />
                  </Modal>
                </Col>
              </Row>
            </PitstopSimpleForm>
          </Col>
        </Row>
      </Wrapper>
    </>
  );
};

AddPartPage.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default observer(AddPartPage);
